import isFunction from 'lodash/isFunction';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { USER_ROLES } from '@/constants';
// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';
import { LOAD_STATUS } from '@/vuex/modules/auth/auth.module';
import { useOrganizationStore } from '@/stores/organization.store';
import { useVocItemsStore } from '@/stores/voc-items.store';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { getTrialData } from '@/vuex/modules/subscription/subscription.utils';
import { getSanitizedUrlParam } from '@/utils/url';
import { getPendoApiKey } from '@/utils/analytics';
import { getActivePinia } from 'pinia';
import { BETA_OPT_INS_SETTING, toBetaOptIns } from '@pendo/middleware';
import { getPermissionString } from './permissions.util';
import { $canOrg } from '@pendo/permissions';
import { isProdEnvironment } from '@/settings/environment.utils.js';

function subCookieIdentifierMap () {
    const isProd = isProdEnvironment();
    const isProdIO = window.appEnvId === 'prod';
    const isLocal = __PROCESS__.LOCAL_ENV;
    let cookieDomain = '.pendo-dev.com';

    if (isProdIO) {
        cookieDomain = '.pendo.io';
    } else if (isProd || isLocal) {
        return {};
    }

    return {
        cookieDomain,
    };
}

const networkLogs = {
    excludeRequestUrls: [
        /https:\/\/firestore\.googleapis\.com\/.*/,
        /.*password.*/,
        /.*login.*/,
        /.*signup.*/,
        /.*token.*/,
    ],
    captureRequestBody: (body) => body,
    captureResponseBody: (body) => body,
};
const partnerAnalyticsAllowedEnvironments = ['prod', 'test', 'dev', 'atlas', 'magic'];

export async function identify () {
    const { visitor, account } = await formatMetadata();
    const allowPartnerAnalyticsForwarding = partnerAnalyticsAllowedEnvironments.includes(__PROCESS__.PENDO_ENV);

    let disableGuides = false;

    try {
        // TEMP TO RESOLVE SEV1
        const instructureSubIds = [
            '4820939051958272', // io sub id
            '5774894566670336', // eu sub id
            '6214176238403584', // au sub id
            '6282400585940992', // jpn sub id

            '4656217648267264', // io - instructure bridge
            '6742282203889664', // io - instructure sandbox
            '4820939051958272', // io - instructure7
            '6750799550152704', // io - instructure10

            '5774894566670336', // eu - instructure oem

            '6214176238403584', // au - instructure oem

            '6282400585940992', // jpn - instructure oem
        ];

        const subscriptionId = store.state.subscription?.active?.id;
        const subName = store.state.subscription?.active?.displayName || '';
        const isInstructureRelatedAEU = instructureSubIds.includes(String(account?.partnerSubId));
        const isInstructureRelatedSub = instructureSubIds.includes(String(subscriptionId));
        const isInstructureRelatedSubNameCheck = subName?.toLowerCase()?.includes('instructure');
        const isInstructureHostname = window.location.hostname.includes('instructure');
        disableGuides = isInstructureRelatedAEU || isInstructureRelatedSubNameCheck || isInstructureRelatedSub || isInstructureHostname;
    } catch(err) {
        console.error(err);
    }

    if (!store.state.auth.initialized) {
        window.pendo.initialize({
            guides: { disabled: disableGuides },
            events: {
                deliverablesLoaded () {
                    const segmentFlags = window.pendo.segmentFlags || [];
                    store.commit('auth/setUserSegmentFlags', { flags: segmentFlags });
                    store.commit('auth/setSegmentFlagsLoadStatus', { status: LOAD_STATUS.COMPLETE });
                },
                guidesFailed () {
                    const segmentFlags = window.pendo.segmentFlags || [];
                    store.commit('auth/setUserSegmentFlags', { flags: segmentFlags });
                    store.commit('auth/setSegmentFlagsLoadStatus', { status: LOAD_STATUS.COMPLETE });
                },
            },
            visitor,
            account,
            allowPartnerAnalyticsForwarding,
            apiKey: getPendoApiKey(),
            ...subCookieIdentifierMap(),
            networkLogs,
        });
        store.commit('auth/setInitialized', { initialized: true });
    } else {
        if (window.pendo && isFunction(window.pendo.identify)) {
            window.pendo.identify({ visitor, account });
        }

        if (window.FS && isFunction(window.FS.identify)) {
            window.FS.identify(visitor.id, pick(visitor, ['email', 'firstName', 'lastName', 'role']));
        }
    }

    // write a shared cookie for help center auth using identify info
    // this probably belongs somewhere else...
    const numDays = 100;
    const duration = new Date();
    duration.setDate(duration.getDate() + numDays);
    sendMarketingPageRegistrationReferralEvent(account, visitor);
    const value = {
        visitorId: visitor.id,
        accountId: account.id,
    };
    const cookieValue = encodeURIComponent(JSON.stringify(value));
    document.cookie = `pendo_identity=${cookieValue}; expires=${duration.toUTCString()}; secure=true; domain=.pendo.io; path=/`;
}

export async function identifyAnonymous () {
    pendo.initialize({
        apiKey: getPendoApiKey(),
        ...subCookieIdentifierMap(),
        networkLogs,
    });
}

export async function formatMetadata () {
    const subscription = store.state.subscription.active;
    const applications = store.getters['apps/list'];

    // When switching subscriptions
    // product lines don't get hydrated in time to be included in the metadata
    await store.dispatch('productLines/loadAll');

    await Promise.allSettled([
        useUserSettingsStore().loadOne({ name: BETA_OPT_INS_SETTING, namespaced: true }),
        store.dispatch('subscription/loadSetting', { setting: BETA_OPT_INS_SETTING, retrieveMetadata: false }),
    ]);

    const visitor = formatVisitorMetadata({ user: store.state.auth.user, subscription });
    const account = formatAccountMetadata({
        subscription,
        applications,
        productLines: store.state.productLines.map,
    });

    return {
        visitor,
        account,
    };
}

function formatVisitorDiscoverPermission () {
    const discoverManager = useVocItemsStore().canEditVocItems;
    const { canCreateVocItems } = useVocItemsStore();
    const isAdmin = store.getters['auth/isAdmin'];

    if (isAdmin) {
        return 'Admin';
    }
    if (discoverManager) {
        return 'Manager';
    }
    if (canCreateVocItems) {
        return 'Contributor';
    }

    return 'Viewer';
}

function formatVisitorOrgRole () {
    const orgAdmin =
        $canOrg()
            .entity('organizationSetting')
            .action('view') &&
        $canOrg()
            .entity('organizationSetting')
            .action('edit');
    const orgContentManager = $canOrg()
        .entity('organizationResources')
        .action('edit');
    const orgReporter =
        $canOrg()
            .entity('organizationMau')
            .action('view') &&
        $canOrg()
            .entity('organizationSummary')
            .action('view');

    if (orgAdmin) {
        return 'Admin';
    }
    const roles = [];
    if (orgContentManager) {
        roles.push('Content Manager');
    }
    if (orgReporter) {
        roles.push('Reporter');
    }

    return roles.join(', ');
}

function formatVisitorMetadata ({ user, subscription }) {
    const isSuperUser = store.getters['auth/isSuper'];
    const username = user.username && user.username.toLowerCase();
    const userFlags = store.getters['auth/flagList'];
    const discoverPermission = formatVisitorDiscoverPermission();
    const discoverGetStartedPreferences = useUserSettingsStore().settingByName(
        'discoverGetStartedPreferences',
        true,
    );
    const isMultiApp = store.getters['apps/isMultiApp'];
    const organization = useOrganizationStore().getActive;
    const windowScreenSizeMetadata = `${window.screen.width}x${window.screen.height}`;
    const viewportMetadata = `${window.innerWidth}x${window.innerHeight}`;
    const discoverJobRole = discoverGetStartedPreferences?.value?.jobRole;
    const appPaOwnership = discoverGetStartedPreferences?.value?.appsPas?.workOn;

    let appOwnership;
    let paOwnership;
    if (appPaOwnership === 'all') {
        if (isMultiApp) appOwnership = true;
        paOwnership = true;
    } else if (appPaOwnership) {
        if (isMultiApp) appOwnership = discoverGetStartedPreferences?.value?.appsPas?.selectedApps?.length > 0;
        paOwnership = discoverGetStartedPreferences?.value?.appsPas?.selectedPas?.length > 0;
    } else if (appPaOwnership === 'unknown') {
        if (isMultiApp) appOwnership = null;
        paOwnership = null;
    }

    let fullname;
    if (user.first && user.last) {
        fullname = `${user.first} ${user.last}`;
    } else if (user.first) {
        fullname = user.first;
    } else {
        fullname = username;
    }

    const isAdmin = store.getters['auth/isAdmin'];
    const isViewOnly = store.getters['auth/isReadOnly'];
    const permissionSummary = `${isAdmin ? 'Admin, ' : ''}${isViewOnly ? 'View Only, ' : ''}${getPermissionString()}`;
    const betaOptIns = [
        ...new Set([
            ...toBetaOptIns(useUserSettingsStore().settingByName(BETA_OPT_INS_SETTING, true)?.value),
            ...toBetaOptIns(store.state.subscription.settings?.[BETA_OPT_INS_SETTING]?.value),
        ]),
    ];

    return {
        id: username,
        role: isSuperUser ? USER_ROLES.SUPER : USER_ROLES.USER,
        email: username,
        firstName: user.first,
        lastName: user.last,
        full_name: fullname,
        utm_source: get(user, 'utmMetadata.utmSource'),
        utm_campaign: get(user, 'utmMetadata.utmCampaign'),
        utm_medium: get(user, 'utmMetadata.utmMedium'),
        utm_content: get(user, 'utmMetadata.utmContent'),
        utm_term: get(user, 'utmMetadata.utmTerm'),
        userFlags,
        last_active_account_id: subscription.name,
        window_screen_size_metadata: windowScreenSizeMetadata,
        viewport_metadata: viewportMetadata,
        user_job_role: user.jobRole,
        user_use_case: user.useCase,
        discover_permission: discoverPermission,
        discover_job_role: discoverJobRole,
        app_pa_ownership: appPaOwnership,
        app_ownership: appOwnership,
        pa_ownership: paOwnership,
        permission_summary: permissionSummary,
        active_organization: get(organization, 'id', ''),
        organization_role: formatVisitorOrgRole(),
        [BETA_OPT_INS_SETTING]: betaOptIns,
    };
}

function formatAccountMetadata ({ subscription, applications, productLines }) {
    if (store.getters['auth/isSuper']) {
        return {
            id: 'pendo-internal',
            impersonationSubName: subscription.name, // leveraged by product-ops and sales-eng in guides
        };
    }

    const subscriptionFlags = store.getters['subscription/featureFlagList'];
    const isPendoFree = store.getters['subscription/isPendoFree'];
    const { trialDaysLeft, trialEndDateISOString, isInTrial } = getTrialData(subscription);
    const hadUpfrontTrial = store.getters['subscription/hadUpfrontTrial'];
    let subscriptionType = 'fullFeature';

    const freeTrialParams = {
        is_paying: true,
        tags: isPendoFree ? ['Pendo Free'] : ['Pendo Core'],
        is_in_trial: isInTrial,
        trial_end_date: trialEndDateISOString !== 0 ? trialEndDateISOString : null,
    };

    if (isPendoFree || isInTrial) {
        Object.assign(freeTrialParams, {
            is_paying: false,
            monthly_value: 0,
            had_upfront_trial: hadUpfrontTrial,
        });

        subscriptionType = 'pendoFree';
    }

    if (isInTrial) {
        Object.assign(freeTrialParams, {
            number_of_trial_days_remaining: Math.floor(trialDaysLeft),
        });

        subscriptionType = 'freeTrial';
    }

    const { disabled } = subscription.featureFlags;
    if (disabled) {
        subscriptionType = 'disabled';
    }

    const applicationCounts = generateApplicationCounts(applications);

    return {
        id: subscription.name,
        subscriptionFlags,
        beta_subscriptionType: subscriptionType, // The data collected for the subscription type isn’t (and shouldn’t) be used long term.
        feedbackEnabled: get(productLines, 'feedback.enabled'),
        coreEnabled: get(productLines, 'core.enabled'),
        validateEnabled: get(productLines, 'voc.metadata.ideas', false),
        horizonEnabled: get(productLines, 'voc.metadata.feedback', false),
        partnerSubId: get(subscription, 'trainingPartnerSubscriptionId', undefined),
        ...freeTrialParams,
        ...applicationCounts,
    };
}

export function generateApplicationCounts (applications = []) {
    const applicationCounts = applications.reduce((acc, app) => {
        const { platform } = app;
        const platformKey = `${platform}AppCount`;

        if (!acc[platformKey]) acc[platformKey] = 0;

        acc[platformKey]++;

        return acc;
    }, {});

    return applicationCounts;
}

export function sendMarketingPageRegistrationReferralEvent (account, visitor) {
    const eventData = {};
    const pageName = getSanitizedUrlParam('pageName');
    const selectedMAU = parseInt(getSanitizedUrlParam('selectedMAU'));
    const planName = getSanitizedUrlParam('planName');
    const paymentFrequency = getSanitizedUrlParam('paymentFrequency');
    if (!pageName && !selectedMAU && !planName && !paymentFrequency) {
        return;
    }

    if (pageName) {
        eventData.register_pageName = pageName;
    }
    if (selectedMAU) {
        eventData.pricingPage_selectedMAU = selectedMAU;
    }
    if (planName) {
        eventData.pricingPage_planName = planName;
    }
    if (paymentFrequency) {
        eventData.pricingPage_paymentFrequency = paymentFrequency;
    }
    eventData.accountId = account.id;
    eventData.visitorId = visitor.id;
    window.pendo.track('marketingPageRegistrationReferral', eventData);
}

export function analytics (store) {
    let userSettingsUnsub = null;

    store.subscribe(({ type, payload }) => {
        if (!userSettingsUnsub && getActivePinia()) {
            userSettingsUnsub = useUserSettingsStore().$onAction(({ name, args, after }) => {
                if (name === 'update') {
                    after(() => {
                        if (args[0]?.name?.startsWith('discoverGetStartedPreferences')) {
                            identify();
                        }
                    });
                }
            });
        }

        switch (type) {
            case 'auth/setProperty': {
                if (!payload.reIdentify) {
                    break;
                }

                if (['first', 'last', 'jobRole', 'useCase'].includes(payload.property)) {
                    identify();
                }
                break;
            }
        }
    });
}
